import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { Plus, Search, Pencil, Trash2, DollarSign, Target, TrendingUp, Handshake, ArrowUpDown } from "lucide-react";
import { DEAL_STAGES, formatCurrency, getDealStageConfig } from "@/types/crm";
import type { Deal, DealStage, Customer } from "@/types/crm";
import DealForm from "@/components/admin/DealForm";
import { logAudit } from "@/utils/auditLog";
import { useAuth } from "@/hooks/useAuth";

type DealWithCustomer = Deal & { customer?: Customer; vehicle?: { year: number; make: string; model: string } | null };

export default function DealsManagement() {
  const [deals, setDeals] = useState<DealWithCustomer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<"number" | "title" | "price" | "stage">("number");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [prefixState, setPrefixState] = useState(localStorage.getItem("deal_number_prefix") || "D");
  const { user } = useAuth();

  // Form mode: null = list, "new" = create, Deal = edit
  const [editingDeal, setEditingDeal] = useState<Deal | null | "new">(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => { 
    loadDeals(); 
    if (searchParams.get("new") === "1") {
      setEditingDeal("new");
      searchParams.delete("new");
      setSearchParams(searchParams);
    }
  }, []);

  const loadDeals = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("deals")
      .select("*, customer:customers(id, first_name, last_name, email, phone), vehicle:vehicles(year, make, model)")
      .order("created_at", { ascending: false });

    if (error) {
      toast.error("Failed to load deals");
    } else {
      setDeals((data as any[]) || []);
    }
    setIsLoading(false);
  };

  const deleteDeal = async (deal: DealWithCustomer) => {
    setDeletingId(deal.id);
    const { error } = await supabase.rpc("delete_deal_and_revert_vehicle", { p_deal_id: deal.id });
    if (error) {
      toast.error("Failed to delete deal");
    } else {
      toast.success("Deal deleted");
      await logAudit({
        entityType: 'deal',
        entityId: deal.id,
        action: 'deleted',
        metadata: { title: deal.title, deal_number: deal.deal_number, customer_name: deal.customer ? `${deal.customer.first_name} ${deal.customer.last_name}` : undefined },
        performedBy: user?.id,
      });
      loadDeals();
    }
    setDeletingId(null);
  };

  // Filtering
  const filtered = deals.filter(d => {
    if (stageFilter !== "all" && d.stage !== stageFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const name = `${d.customer?.first_name || ""} ${d.customer?.last_name || ""}`.toLowerCase();
      const veh = d.vehicle ? `${d.vehicle.year} ${d.vehicle.make} ${d.vehicle.model}`.toLowerCase() : "";
      if (!name.includes(q) && !d.deal_number.toLowerCase().includes(q) && !d.title.toLowerCase().includes(q) && !veh.includes(q)) return false;
    }
    return true;
  });

  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortOrder(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
  };

  const sortedDeals = [...filtered].sort((a, b) => {
    let aVal: any;
    let bVal: any;

    if (sortField === "number") {
      aVal = a.deal_number.toLowerCase();
      bVal = b.deal_number.toLowerCase();
    } else if (sortField === "title") {
      aVal = a.title.toLowerCase();
      bVal = b.title.toLowerCase();
    } else if (sortField === "price") {
      aVal = a.sale_price || 0;
      bVal = b.sale_price || 0;
    } else { // stage
      aVal = a.stage.toLowerCase();
      bVal = b.stage.toLowerCase();
    }

    if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
    if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  // KPIs
  const activeDeals = deals.filter(d => d.stage !== "funded" && d.stage !== "lost");
  const activeValue = activeDeals.reduce((s, d) => s + (d.sale_price || 0), 0);
  const fundedThisMonth = deals.filter(d => {
    if (d.stage !== "funded" && d.stage !== "delivered") return false;
    const dateStr = (d as any).delivery_date || d.closed_at || d.updated_at;
    const closed = new Date(dateStr);
    const now = new Date();
    return closed.getMonth() === now.getMonth() && closed.getFullYear() === now.getFullYear();
  });
  const avgGross = fundedThisMonth.length > 0
    ? fundedThisMonth.reduce((s, d) => s + (d.front_gross || 0), 0) / fundedThisMonth.length
    : 0;

  // If in form mode, render the form
  if (editingDeal !== null) {
    return (
      <DealForm
        deal={editingDeal === "new" ? null : editingDeal}
        onBack={() => setEditingDeal(null)}
        onSaved={() => { setEditingDeal(null); loadDeals(); }}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-3xl font-heading font-bold">Deals Management</h1>
          <p className="text-muted-foreground">Manage deals, worksheets, and bill-of-sale data</p>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <div className="flex items-center gap-2 border rounded-lg px-3 py-1.5 bg-card">
            <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap">Prefix:</span>
            <input 
              type="text" 
              maxLength={3}
              value={prefixState} 
              onChange={(e) => {
                const val = e.target.value.toUpperCase().replace(/[^A-Z]/g, "");
                localStorage.setItem("deal_number_prefix", val || "D");
                setPrefixState(val || "D");
              }}
              className="w-12 h-6 text-xs text-center border rounded font-mono font-bold bg-muted"
            />
          </div>
          <Button onClick={() => setEditingDeal("new")} className="flex-1 sm:flex-none">
            <Plus className="h-4 w-4 mr-2" /> New Deal
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Handshake className="h-3 w-3" /> Total Deals</div>
            <div className="text-2xl font-bold">{deals.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><DollarSign className="h-3 w-3" /> Active Value</div>
            <div className="text-2xl font-bold">{formatCurrency(activeValue)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><TrendingUp className="h-3 w-3" /> Funded This Month</div>
            <div className="text-2xl font-bold text-green-600">{fundedThisMonth.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Target className="h-3 w-3" /> Avg Front Gross</div>
            <div className="text-2xl font-bold">{formatCurrency(avgGross)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search deals..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className="w-full sm:w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {DEAL_STAGES.map(s => (<SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">Loading deals...</div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">{search || stageFilter !== "all" ? "No deals match your filters." : "No deals yet. Create your first deal!"}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block">
            <Card>
              <Table>
                <TableHeader>
                  <TableRow className="select-none">
                    <TableHead onClick={() => toggleSort("number")} className="cursor-pointer hover:bg-muted">
                      <span className="flex items-center gap-1">Deal # <ArrowUpDown className="h-3 w-3" /></span>
                    </TableHead>
                    <TableHead onClick={() => toggleSort("title")} className="cursor-pointer hover:bg-muted">
                      <span className="flex items-center gap-1">Customer <ArrowUpDown className="h-3 w-3" /></span>
                    </TableHead>
                    <TableHead>Vehicle</TableHead>
                    <TableHead onClick={() => toggleSort("stage")} className="cursor-pointer hover:bg-muted">
                      <span className="flex items-center gap-1">Stage <ArrowUpDown className="h-3 w-3" /></span>
                    </TableHead>
                    <TableHead onClick={() => toggleSort("price")} className="text-right cursor-pointer hover:bg-muted">
                      <span className="flex items-center justify-end gap-1">Sale Price <ArrowUpDown className="h-3 w-3" /></span>
                    </TableHead>
                    <TableHead className="text-right">Front Gross</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedDeals.map(deal => {
                    const stageConfig = getDealStageConfig(deal.stage);
                    return (
                      <TableRow key={deal.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setEditingDeal(deal)}>
                        <TableCell className="font-mono text-xs">{deal.deal_number}</TableCell>
                        <TableCell>
                          <div className="font-medium">{deal.customer?.first_name} {deal.customer?.last_name}</div>
                          <div className="text-xs text-muted-foreground truncate max-w-[200px]">{deal.title}</div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {deal.vehicle ? `${deal.vehicle.year} ${deal.vehicle.make} ${deal.vehicle.model}` : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell>
                          <Badge style={{ backgroundColor: stageConfig.color, color: "#fff" }}>{stageConfig.label}</Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(deal.sale_price)}</TableCell>
                        <TableCell className="text-right font-medium text-green-600">{formatCurrency(deal.front_gross)}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{formatDistanceToNow(new Date(deal.created_at), { addSuffix: true })}</TableCell>
                        <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                          <div className="flex justify-end gap-1">
                            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setEditingDeal(deal)}><Pencil className="h-3.5 w-3.5" /></Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="outline" size="icon" className="h-8 w-8 text-destructive hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete this deal?</AlertDialogTitle>
                                  <AlertDialogDescription>This will permanently remove deal {deal.deal_number}. This action cannot be undone.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => deleteDeal(deal)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {sortedDeals.map(deal => {
              const stageConfig = getDealStageConfig(deal.stage);
              return (
                <Card key={deal.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setEditingDeal(deal)}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-semibold">{deal.customer?.first_name} {deal.customer?.last_name}</div>
                        <div className="text-xs text-muted-foreground">{deal.title}</div>
                      </div>
                      <Badge style={{ backgroundColor: stageConfig.color, color: "#fff" }} className="text-xs shrink-0">{stageConfig.label}</Badge>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="font-mono text-xs text-muted-foreground">{deal.deal_number}</span>
                      <span className="font-bold">{formatCurrency(deal.sale_price)}</span>
                    </div>
                    {deal.vehicle && (
                      <div className="text-xs text-muted-foreground">{deal.vehicle.year} {deal.vehicle.make} {deal.vehicle.model}</div>
                    )}
                    <div className="flex justify-between items-center pt-1">
                      <span className="text-xs text-muted-foreground">{formatDistanceToNow(new Date(deal.created_at), { addSuffix: true })}</span>
                      <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                        <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setEditingDeal(deal)}><Pencil className="h-3 w-3" /></Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="icon" className="h-7 w-7 text-destructive"><Trash2 className="h-3 w-3" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete this deal?</AlertDialogTitle>
                              <AlertDialogDescription>Permanently delete {deal.deal_number}?</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteDeal(deal)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
